
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from '@/types/supabase';
import ServiceCard from '@/components/ServiceCard';
import AdUnit from '@/components/ads/AdUnit';
import AfterIdle from '@/components/util/AfterIdle';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@/config/featureFlags';
import { Button } from "@/components/ui/button";

interface ProvidersListWithAdsProps {
  providers: Provider[];
  isLoading: boolean;
  searchQuery?: string;
  selectedCategory?: string;
  hasMore?: boolean;
  onLoadMore?: () => void;
  columnsPerRow: number;
}

const ProvidersListWithAds: React.FC<ProvidersListWithAdsProps> = ({
  providers,
  isLoading,
  searchQuery,
  selectedCategory,
  hasMore = false,
  onLoadMore,
  columnsPerRow,
}) => {
  const { t } = useTranslation('explore');
  const isAdsEnabled = useFeatureFlag(FEATURE_FLAGS.ENABLE_ADS);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="animate-pulse rounded-md border border-brand-cloud bg-white p-6 shadow-sm">
            <div className="mb-4 h-48 w-full rounded-md bg-brand-cloud"></div>
            <div className="mb-2 h-4 rounded bg-brand-cloud"></div>
            <div className="mb-4 h-4 w-3/4 rounded bg-brand-cloud"></div>
            <div className="h-10 rounded bg-brand-cloud"></div>
          </div>
        ))}
      </div>
    );
  }

  if (providers.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="mb-2 public-card-title">{t("providers.noResults.title")}</h3>
        <p className="text-brand-slate">{t("providers.noResults.description")}</p>
      </div>
    );
  }

  const itemsPerRow = columnsPerRow > 0 ? columnsPerRow : 3;
  const rowsPerAd = 3;
  const itemsBeforeAd = itemsPerRow * rowsPerAd;

  return (
    <>
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6" data-test-responsive="explore-providers-grid">
        {providers.map((provider, index) => {
          const shouldShowAd = 
            isAdsEnabled &&
            itemsBeforeAd > 0 &&
            (index + 1) % itemsBeforeAd === 0 &&
            index !== providers.length - 1 &&
            providers.length > itemsBeforeAd; 
          
          return (
            <React.Fragment key={provider.id || index}>
              <ServiceCard
                id={provider.id}
                name={provider.name}
                image={provider.image_url || ''}
                description={provider.description || ''}
                rating={provider.rating || 0}
                user_id={provider.user_id}
                isVerified={provider.is_verified || false}
                isFeatured={provider.is_featured || false}
                categories={provider.categories || []}
                suburbs={provider.suburbs || []}
                websiteUrl={provider.website_url ?? undefined}
                pageType="explore"
                searchQuery={searchQuery}
                categoryFilter={selectedCategory}
                userProfileVerified={provider.user_profile_verified}
                whatsapp_number={provider.whatsapp_number}
              />
              
              {shouldShowAd && (
                <AfterIdle timeoutMs={1200}>
                  <div className="col-span-full">
                    <div className="flex h-full flex-col justify-center overflow-hidden rounded-md border border-brand-cloud bg-white p-4 shadow-sm">
                      <AdUnit 
                        adSlot="7965312393" 
                        adFormat="BANNER" 
                        className="mx-auto w-full"
                      />
                    </div>
                  </div>
                </AfterIdle>
              )}
            </React.Fragment>
          );
        })}
      </div>
      
      {hasMore && onLoadMore && (
        <div className="flex justify-center mt-8" data-test-responsive="explore-load-more">
          <Button 
            onClick={onLoadMore}
            variant="outline"
            size="lg"
            className="px-8"
          >
            {t("providers.loadMore")}
          </Button>
        </div>
      )}
    </>
  );
};

export default ProvidersListWithAds;
